/**
 * BatchVoucherLanding - Coupling WNC
 *
 * Modal overlay for voucher batch smart links, rendered on top of the Homepage.
 * Users land here from /claim/:code. The homepage is fully visible behind
 * a semi-transparent backdrop. Clicking outside or the X button dismisses
 * the modal, saving the voucher to sessionStorage so the PendingVoucherBanner
 * persists across the site.
 */

import React, { useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import {
  SEO,
  VoucherClaimCard,
  savePendingVoucher,
  setAcquisitionSource,
} from '@coupling/ui';
import { seoConfig } from '../config/seo';
import { analyticsService, AnalyticsEvent } from '../utils/analytics';

const appUrl = import.meta.env.VITE_APP_URL || 'https://app.cplng.org';

const BATCH_VOUCHER_LANDING = gql`
  query BatchVoucherLanding($shareCode: String!) {
    batchSmartLink(shareCode: $shareCode) {
      merchantName
      tokenName
      tokenSymbol
      tokenCurrency
      exchangeRate
      amountPerVoucher
      quantity
      vouchersAvailable
      memo
      label
      expiresAt
      status
    }
  }
`;

const BatchVoucherLanding: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const source = searchParams.get('source');

  const { data, loading, error } = useQuery(BATCH_VOUCHER_LANDING, {
    variables: { shareCode: code },
    skip: !code,
    fetchPolicy: 'network-only',
  });

  // Store acquisition source on mount
  useEffect(() => {
    if (source) {
      setAcquisitionSource(source);
    }
  }, [source]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const batch = data?.batchSmartLink;

  // Track voucher view when batch data loads
  useEffect(() => {
    if (!batch || !code) return;
    analyticsService.logEvent(AnalyticsEvent.VOUCHER_VIEW, {
      merchant_name: batch.merchantName,
      token_symbol: batch.tokenSymbol,
      share_code: code,
    });
  }, [batch, code]);

  // Save pending voucher once data loads
  useEffect(() => {
    if (!batch || !code) return;

    let displayAmount: string;
    if (batch.exchangeRate > 0) {
      const fiat = batch.amountPerVoucher / batch.exchangeRate;
      displayAmount = `$${fiat.toFixed(2)}`;
    } else {
      displayAmount = `${batch.amountPerVoucher.toLocaleString()} ${batch.tokenSymbol}`;
    }

    const sourceParam = source ? `?source=${encodeURIComponent(source)}` : '';
    savePendingVoucher({
      shareCode: code,
      merchantName: batch.merchantName,
      displayAmount,
      claimUrl: `${appUrl}/b/${code}${sourceParam}`,
    });
  }, [batch, code, source]);

  const dismissModal = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dismissModal]);

  const sourceParam = source ? `?source=${encodeURIComponent(source)}` : '';
  const claimUrl = code ? `${appUrl}/b/${code}${sourceParam}` : `${appUrl}/signup`;

  // Loading — show spinner overlay
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white" />
      </div>
    );
  }

  // Error or invalid code — show error card then dismiss
  if (error || !batch) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
        onClick={dismissModal}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <SEO
            title={`Invalid Link - ${seoConfig.siteName}`}
            description="This voucher link is invalid or has expired."
            canonical={seoConfig.siteUrl}
            ogImage={seoConfig.defaultOgImage}
          />
          <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-3">Invalid Link</h2>
          <p className="text-neutral-600 mb-6">
            This voucher link is invalid or has expired. Please check with the person who shared it with you.
          </p>
          <button
            onClick={dismissModal}
            className="inline-flex items-center px-6 py-3 text-base font-medium rounded-full text-white bg-brand-primary hover:bg-brand-primary-dark transition-colors"
          >
            Browse the Site
          </button>
        </div>
      </div>
    );
  }

  const fiatValue = batch.exchangeRate > 0
    ? batch.amountPerVoucher / batch.exchangeRate
    : null;

  const pageTitle = `Claim free tokens from ${batch.merchantName} - ${seoConfig.siteName}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={dismissModal}
    >
      <SEO
        title={pageTitle}
        description={`${batch.merchantName} is giving away ${batch.tokenSymbol} tokens. Claim yours free and spend at WNC businesses.`}
        canonical={`${seoConfig.siteUrl}/claim/${code}`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/claim/${code}`}
      />

      {/* Modal card — stop click propagation so clicking inside doesn't dismiss */}
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={dismissModal}
          className="absolute top-4 right-4 p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-full transition-colors z-10"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Gift icon + heading */}
        <div className="text-center pt-8 px-6">
          <svg className="w-16 h-16 text-blue-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
          </svg>
          <h2 className="text-2xl font-bold text-neutral-900 mb-1">
            Claim Free Tokens!
          </h2>
          <p className="text-neutral-600 text-sm">
            {batch.merchantName} is giving away tokens
          </p>
        </div>

        {/* Voucher Details Card */}
        <div className="px-6 pt-4">
          <VoucherClaimCard
            merchantName={batch.merchantName}
            tokenName={batch.tokenName}
            tokenSymbol={batch.tokenSymbol}
            amountPerVoucher={batch.amountPerVoucher}
            fiatValue={fiatValue}
            currency={batch.tokenCurrency}
            quantity={batch.quantity}
            vouchersAvailable={batch.vouchersAvailable}
            memo={batch.memo}
            label={batch.label}
            expiresAt={batch.expiresAt}
            status={batch.status}
          />
        </div>

        {/* CTA Button */}
        <div className="px-6 pt-4 pb-8">
          <a
            href={claimUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-6 py-4 text-lg font-semibold rounded-full text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            onClick={() => analyticsService.logEvent(AnalyticsEvent.CTA_CLICK, {
              cta_name: 'claim_your_tokens',
              merchant_name: batch.merchantName,
              share_code: code,
            })}
          >
            Claim Your Tokens
          </a>
          <p className="text-center text-sm text-neutral-500 mt-3">
            Free to claim — sign up or log in to receive your tokens
          </p>
        </div>
      </div>
    </div>
  );
};

export default BatchVoucherLanding;
